import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  User, Truck, FileText, Upload, Calendar, Package, Loader2, Home, PenLine
} from 'lucide-react';
import { toast } from 'sonner';

const PortaleFamiglia = () => {
  const { API } = useAuth();
  const [profilo, setProfilo] = useState(null);
  const [distribuzioni, setDistribuzioni] = useState([]);
  const [documenti, setDocumenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [tipoDoc, setTipoDoc] = useState('isee');
  const [file, setFile] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [p, d, doc] = await Promise.all([
        axios.get(`${API}/portale-famiglia/profilo`),
        axios.get(`${API}/portale-famiglia/distribuzioni`),
        axios.get(`${API}/portale-famiglia/documenti`),
      ]);
      setProfilo(p.data);
      setDistribuzioni(d.data);
      setDocumenti(doc.data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore nel caricamento'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/portale-famiglia/documenti?tipo=${tipoDoc}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documento caricato');
      setFile(null);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, "Errore nell'upload"));
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString('it-IT'); } catch { return d; }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="portale-famiglia">
      <div>
        <h1 className="text-xl sm:text-2xl font-heading font-bold">Il Mio Portale</h1>
        <p className="text-xs sm:text-sm text-muted-foreground">Benvenuto/a, {profilo?.nome_referente}</p>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Truck className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{distribuzioni.length}</p>
              <p className="text-xs text-muted-foreground">Distribuzioni</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{documenti.length}</p>
              <p className="text-xs text-muted-foreground">Documenti</p>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-2 sm:col-span-1">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <User className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{profilo?.totale_componenti || 0}</p>
              <p className="text-xs text-muted-foreground">Componenti</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="distribuzioni">
        <TabsList className="w-full grid grid-cols-3">
          <TabsTrigger value="distribuzioni">Distribuzioni</TabsTrigger>
          <TabsTrigger value="documenti">Documenti</TabsTrigger>
          <TabsTrigger value="profilo">Profilo</TabsTrigger>
        </TabsList>

        {/* Distribuzioni ricevute */}
        <TabsContent value="distribuzioni">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Storico Distribuzioni Ricevute</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {distribuzioni.length === 0 ? (
                <p className="text-center text-muted-foreground p-6 text-sm">Nessuna distribuzione ricevuta</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Prodotti</TableHead>
                      <TableHead className="text-center">Firma</TableHead>
                      <TableHead className="text-right">Valore</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {distribuzioni.map((d) => (
                      <TableRow key={d.id}>
                        <TableCell className="text-sm">{formatDate(d.data)}</TableCell>
                        <TableCell className="text-sm">
                          {(d.prodotti || []).map(p => p.nome_prodotto || p.prodotto_id).join(', ')}
                        </TableCell>
                        <TableCell className="text-center">
                          {d.firma_ritiro ? (
                            <Badge variant="outline" className="text-green-600 border-green-600 text-[10px]"><PenLine className="w-3 h-3 mr-1" />Si</Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground text-[10px]">No</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-medium">{(d.valore_totale || 0).toFixed(2)} EUR</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documenti */}
        <TabsContent value="documenti">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Carica Documento</CardTitle>
                <CardDescription>Carica i tuoi documenti (ISEE, carta identita, ecc.)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Tipo</Label>
                    <Select value={tipoDoc} onValueChange={setTipoDoc}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="isee">ISEE</SelectItem>
                        <SelectItem value="carta_identita">Carta d'Identita</SelectItem>
                        <SelectItem value="stato_famiglia">Stato di Famiglia</SelectItem>
                        <SelectItem value="altro">Altro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>File</Label>
                    <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0])} />
                  </div>
                </div>
                <Button onClick={handleUpload} disabled={!file || uploading} className="w-full" data-testid="upload-doc-btn">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  Carica Documento
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">I Miei Documenti</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {documenti.length === 0 ? (
                  <p className="text-center text-muted-foreground p-6 text-sm">Nessun documento caricato</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead>File</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {documenti.map((d) => (
                        <TableRow key={d.id}>
                          <TableCell><Badge variant="outline">{d.tipo_documento}</Badge></TableCell>
                          <TableCell className="text-sm">{formatDate(d.data_caricamento || d.created_at)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{d.original_filename || d.filename}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Profilo */}
        <TabsContent value="profilo">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Il Mio Nucleo Familiare</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {profilo && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">Referente</p>
                    <p className="font-medium">{profilo.nome_referente}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">Codice</p>
                    <p className="font-medium">{profilo.codice_famiglia}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">Adulti</p>
                    <p className="font-medium">{profilo.numero_adulti}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">Bambini/Ragazzi</p>
                    <p className="font-medium">{(profilo.bambini_0_3||0) + (profilo.bambini_4_5||0) + (profilo.bambini_6_12||0) + (profilo.ragazzi_13_17||0)}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">Anziani</p>
                    <p className="font-medium">{profilo.anziani || 0}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">Totale</p>
                    <p className="font-medium">{profilo.totale_componenti}</p>
                  </div>
                  {profilo.indirizzo && (
                    <div className="col-span-2 p-3 bg-muted rounded-lg">
                      <p className="text-xs text-muted-foreground">Indirizzo</p>
                      <p className="font-medium">{profilo.indirizzo}{profilo.comune ? `, ${profilo.comune}` : ''}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PortaleFamiglia;
